/**
 * Visitor Time-Based Access Control System - TypeScript Types
 *
 * This file defines the TypeScript interfaces and types for the visitor
 * time-based access control system with 15-minute entry window and credit system.
 */

// Visitor state machine states
export type VisitorState = 'awaiting_entry' | 'inside' | 'exited' | 'expired' | 'consumed';

// Visitor access type
export type VisitorAccessType = 'single_visit' | 'temporary';

/**
 * Visitor Metadata Interface
 *
 * This interface defines all metadata fields stored in the role_metadata JSONB column
 * for visitor and service_provider roles.
 */
export interface VisitorMetadata {
  // Legacy fields (pre-existing)
  authorized_by_name?: string;
  vehicle_plate?: string;
  purpose?: string;
  unit?: string;

  // Visitor time-based access control fields
  visitor_state?: VisitorState;
  access_type?: VisitorAccessType;
  entry_credits_total?: number;
  entry_credits_used?: number;
  entry_window_minutes?: number; // Default: 15 minutes
  entry_window_expires_at?: string; // ISO8601 timestamp
  access_expires_at?: string; // ISO8601 timestamp (11:59 PM same day)
  first_entry_at?: string; // ISO8601 timestamp
  last_entry_at?: string; // ISO8601 timestamp
  last_exit_at?: string; // ISO8601 timestamp
  entry_zone_id?: string;
  entry_zone_name?: string;
  exit_zone_id?: string;
  exit_zone_name?: string;
}

/**
 * Helper functions for visitor state machine
 */

/**
 * Check if visitor is in active states (can potentially access zones)
 */
export function isVisitorActive(state?: VisitorState): boolean {
  return state === 'awaiting_entry' || state === 'inside';
}

/**
 * Check if visitor has expired or consumed their access
 */
export function isVisitorInactive(state?: VisitorState): boolean {
  return state === 'expired' || state === 'consumed' || state === 'exited';
}

/**
 * Get human-readable label for visitor state (Portuguese)
 */
export function getVisitorStateLabel(state?: VisitorState): string {
  switch (state) {
    case 'awaiting_entry':
      return 'Aguardando Entrada';
    case 'inside':
      return 'Dentro';
    case 'exited':
      return 'Saiu';
    case 'expired':
      return 'Expirado';
    case 'consumed':
      return 'Consumido';
    default:
      return 'Desconhecido';
  }
}

/**
 * Get color class for visitor state badge
 */
export function getVisitorStateBadgeColor(state?: VisitorState): string {
  switch (state) {
    case 'awaiting_entry':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'inside':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'exited':
      return 'bg-gray-100 text-gray-800 border-gray-300';
    case 'expired':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'consumed':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
}

/**
 * Get icon name for visitor state (Lucide icons)
 */
export function getVisitorStateIcon(state?: VisitorState): string {
  switch (state) {
    case 'awaiting_entry':
      return 'Clock';
    case 'inside':
      return 'CheckCircle';
    case 'exited':
      return 'LogOut';
    case 'expired':
      return 'XCircle';
    case 'consumed':
      return 'AlertCircle';
    default:
      return 'HelpCircle';
  }
}

/**
 * Get human-readable label for access type (Portuguese)
 */
export function getAccessTypeLabel(type?: VisitorAccessType): string {
  switch (type) {
    case 'single_visit':
      return 'Visita Única';
    case 'temporary':
      return 'Temporário';
    default:
      return 'Não Especificado';
  }
}

/**
 * Calculate time remaining in entry window (in milliseconds)
 * Returns null if window has expired or no expiration time set
 */
export function getEntryWindowTimeRemaining(expiresAt?: string): number | null {
  if (!expiresAt) return null;

  const expirationTime = new Date(expiresAt).getTime();
  const now = Date.now();
  const remaining = expirationTime - now;

  return remaining > 0 ? remaining : null;
}

/**
 * Format time remaining in human-readable format (Portuguese)
 * @param milliseconds Time remaining in milliseconds
 * @returns Formatted string like "12 min 30 seg" or "Expirado"
 */
export function formatTimeRemaining(milliseconds: number | null): string {
  if (milliseconds === null || milliseconds <= 0) {
    return 'Expirado';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Check if entry window is expiring soon (< 5 minutes)
 */
export function isEntryWindowExpiringSoon(expiresAt?: string): boolean {
  const remaining = getEntryWindowTimeRemaining(expiresAt);
  if (remaining === null) return false;

  const FIVE_MINUTES_MS = 5 * 60 * 1000;
  return remaining < FIVE_MINUTES_MS;
}

/**
 * Calculate credits remaining
 */
export function getCreditsRemaining(total?: number, used?: number): number {
  if (total === undefined || used === undefined) return 0;
  return Math.max(0, total - used);
}

/**
 * Validation helper: Check if visitor metadata has required fields for time-based access
 */
export function hasVisitorTimeBasedAccessFields(metadata?: VisitorMetadata): boolean {
  return !!(
    metadata?.visitor_state &&
    metadata?.access_type &&
    metadata?.entry_window_expires_at &&
    metadata?.access_expires_at &&
    metadata?.entry_credits_total !== undefined
  );
}

/**
 * Calculate duration between entry and exit in milliseconds
 * Returns null if either timestamp is missing
 */
export function getVisitDuration(firstEntryAt?: string, lastExitAt?: string): number | null {
  if (!firstEntryAt || !lastExitAt) return null;

  const entryTime = new Date(firstEntryAt).getTime();
  const exitTime = new Date(lastExitAt).getTime();

  return exitTime - entryTime;
}

/**
 * Format visit duration in human-readable format (Portuguese)
 * @param milliseconds Duration in milliseconds
 * @returns Formatted string like "1h 23min" or "45min" or "5seg"
 */
export function formatVisitDuration(milliseconds: number | null): string {
  if (milliseconds === null || milliseconds < 0) {
    return 'N/A';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  } else if (minutes > 0) {
    return `${minutes}min`;
  } else {
    return `${seconds}seg`;
  }
}
